<template>
  <transition name="ask-a-question-image-preview" appear>
    <div
      v-if="open"
      ref="dialogRef"
      class="ask-a-question-image-preview"
      role="dialog"
      aria-modal="true"
      :aria-label="imageName"
      tabindex="-1"
      data-testid="ask-a-question-attachment-image-preview"
      @mousedown.self="emit('close')"
    >
      <div class="ask-a-question-image-preview__frame">
        <div class="ask-a-question-image-preview__card">
          <button
            ref="closeRef"
            type="button"
            class="ask-a-question-image-preview__close"
            aria-label="Close image preview"
            data-testid="ask-a-question-attachment-image-preview-close"
            @click="emit('close')"
          >
            ×
          </button>
          <img
            class="ask-a-question-image-preview__image"
            :src="imageSrc"
            :alt="imageName"
            data-testid="ask-a-question-attachment-image-preview-img"
          />
        </div>
        <p
          class="ask-a-question-image-preview__filename"
          :title="imageName"
          data-testid="ask-a-question-attachment-image-preview-filename"
        >
          {{ imageName }}
        </p>
      </div>
    </div>
  </transition>
</template>

<script setup>
import { nextTick, onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  imageSrc: {
    type: String,
    default: "",
  },
  imageName: {
    type: String,
    default: "Image preview",
  },
});

const emit = defineEmits(["close"]);

const dialogRef = ref(null);
const closeRef = ref(null);
let previouslyFocused = null;

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getFocusableElements = () => {
  if (!dialogRef.value) {
    return [];
  }
  return Array.from(dialogRef.value.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
    (element) => element instanceof HTMLElement && element.offsetParent !== null
  );
};

const restoreFocus = () => {
  if (previouslyFocused && typeof previouslyFocused.focus === "function") {
    previouslyFocused.focus();
  }
  previouslyFocused = null;
};

const focusDialog = () => {
  nextTick(() => {
    const focusable = getFocusableElements();
    if (focusable.length) {
      focusable[0].focus();
      return;
    }
    dialogRef.value?.focus();
  });
};

const handleKeydown = (event) => {
  if (!props.open) {
    return;
  }
  if (event.key === "Escape") {
    emit("close");
    return;
  }
  if (event.key !== "Tab") {
    return;
  }

  const focusable = getFocusableElements();
  if (focusable.length === 0) {
    event.preventDefault();
    dialogRef.value?.focus();
    return;
  }
  if (focusable.length === 1) {
    event.preventDefault();
    focusable[0].focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  const active = document.activeElement;

  if (event.shiftKey) {
    if (active === first || active === dialogRef.value) {
      event.preventDefault();
      last.focus();
    }
    return;
  }

  if (active === last) {
    event.preventDefault();
    first.focus();
  }
};

watch(
  () => props.open,
  (isOpen) => {
    if (typeof document === "undefined") {
      return;
    }
    document.body.style.overflow = isOpen ? "hidden" : "";
    if (isOpen) {
      previouslyFocused = document.activeElement;
      focusDialog();
    } else {
      restoreFocus();
    }
  },
  { immediate: true }
);

onMounted(() => {
  if (typeof window !== "undefined") {
    window.addEventListener("keydown", handleKeydown);
  }
});

onUnmounted(() => {
  if (typeof window !== "undefined") {
    window.removeEventListener("keydown", handleKeydown);
  }
  if (typeof document !== "undefined") {
    document.body.style.overflow = "";
  }
  restoreFocus();
});
</script>

<style scoped>
.ask-a-question-image-preview {
  position: fixed;
  inset: 0;
  z-index: 5000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(17, 24, 39, 0.72);
  box-sizing: border-box;
}

.ask-a-question-image-preview:focus {
  outline: none;
}

.ask-a-question-image-preview__frame {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: min(920px, calc(100vw - 48px));
  max-height: calc(100vh - 48px);
}

.ask-a-question-image-preview__card {
  position: relative;
  display: flex;
  max-width: 100%;
  max-height: calc(100vh - 88px);
  overflow: auto;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(17, 24, 39, 0.28);
}

.ask-a-question-image-preview__close {
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.96);
  color: #4b5563;
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(17, 24, 39, 0.12);
}

.ask-a-question-image-preview__close:hover {
  background: #ffffff;
  color: #111827;
}

.ask-a-question-image-preview__close:focus-visible {
  outline: 2px solid #6366f1;
  outline-offset: 2px;
}

.ask-a-question-image-preview__image {
  display: block;
  max-width: 100%;
  max-height: calc(100vh - 88px);
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 16px;
}

.ask-a-question-image-preview__filename {
  margin: 10px 0 0;
  max-width: 100%;
  font-family: Inter, sans-serif;
  font-size: 13px;
  line-height: 18px;
  font-weight: 400;
  color: #f3f4f6;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.ask-a-question-image-preview-enter-active,
.ask-a-question-image-preview-leave-active {
  transition: opacity 180ms ease;
}

.ask-a-question-image-preview-enter,
.ask-a-question-image-preview-leave-to {
  opacity: 0;
}
</style>
